import { rem } from 'polished'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import type { RouteComponentProps } from 'react-router'
import styled from 'styled-components'

import PublicTripPageHeader from './PublicTripPageHeader'
import PublicTripParentHeader from './PublicTripParentHeader'
import PublicTripSummary from './PublicTripSummary'
import TripItinerary from '../trip/Itinerary/TripItinerary'

import {
  tripLoginModalSignUpDismiss,
  tripLoginModalSignUpView,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import Breakpoint from 'components/Common/Breakpoint'
import HeadData from 'components/Common/HeadData/HeadData'
import FloatingActionButton from 'LuxKit/Button/FloatingActionButton'
import LineHeartIcon from 'LuxKit/Icons/line/LineHeartIcon'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import ModalProvider from 'contexts/ModalProvider'
import usePendingLoginHandler from 'hooks/usePendingLoginHandler'
import zIndex from 'styles/tools/z-index'
import FloatingViewModeToggle from 'tripPlanner/components/FloatingViewModeToggle'
import HeaderBasic from 'tripPlanner/components/Header/HeaderBasic'
import Redirect from 'tripPlanner/components/Redirect'
import { DesktopTripMap } from 'tripPlanner/components/TripMaps/DesktopTripMap'
import TripModal from 'tripPlanner/components/TripModal'
import CopyPublicTrip from 'tripPlanner/components/TripModal/PublicTrip/CopyPublicTrip'
import { headerHeight } from 'tripPlanner/config'
import HeaderInViewContextProvider from 'tripPlanner/contexts/HeaderInViewContext'
import type { TripContextValue } from 'tripPlanner/contexts/TripContext'
import TripContext from 'tripPlanner/contexts/TripContext'
import { ViewModeContextProvider } from 'tripPlanner/contexts/ViewModeContext'
import { usePublicTrip } from 'tripPlanner/hooks/api/publicTrip'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import LoadingPage from 'tripPlanner/pages/Loading'
import type { FullTrip } from 'tripPlanner/types/common'
import { clamp } from 'lib/maths/mathUtils'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { setLeftPaneWidth } from 'tripPlanner/reducers/actions'
import PaneResizer from 'tripPlanner/components/PaneResizer'
import { MobileTripMap } from 'tripPlanner/components/TripMaps/MobileTripMap'

const LEFT_PANE_WIDTH_VAR = '--leftPaneWidth'

const FloatingContainer = styled.div`
  position: fixed;
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: ${rem(8)};
  z-index: ${zIndex.floatingPanel};
  left: ${rem(20)};
  right: ${rem(20)};
  bottom: ${rem(20)};
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }

  @media print {
    display: none;
  }
`

// Making space for the Genesys chat icon
const Content = styled.div`
  container-type: inline-size;

  ${mediaQueryUp.largeDesktop} {
    padding-bottom: ${rem(64)};
  }
`

const MapContainer = styled.div`
  position: sticky;
  z-index: 0;
  top: ${headerHeight.sm};
  height: calc(100vh - ${headerHeight.sm});

  ${mediaQueryUp.desktop} {
    top: ${headerHeight.lg};
    height: calc(100vh - ${headerHeight.lg});
  }

  ${mediaQueryDown.tablet} {
    display: none;
  }
`

const Root = styled.div`
  display: grid;
  position: relative;
  grid-template-columns: clamp(500px, var(${LEFT_PANE_WIDTH_VAR}), calc(100% - 500px)) 0 1fr;
  background: ${(props) => props.theme.palette.neutral.default.eight};
  min-height: calc(var(--vh) * 100);

  ${mediaQueryDown.tablet} {
    grid-template-columns: 100%;
  }
`

type MatchParams = { tripId: string, tab: string }
interface Props extends RouteComponentProps<MatchParams> {}

const trackTripLoginModalSignUpView = () => {
  fireInteractionEvent(tripLoginModalSignUpView('save_public_trip'))
}
const trackTripLoginModalSignUpDismiss = () => {
  fireInteractionEvent(tripLoginModalSignUpDismiss('save_public_trip'))
}

function PublicTripParent(props: Props) {
  const intl = useIntl()
  const { match } = props
  const tripId = match.params.tripId

  const openModal = useOpenModal()
  const dispatch = useAppDispatch()

  const {
    isLoading,
    isError,
    data: publicTrip,
    error: tripError,
  } = usePublicTrip(tripId)

  const onSave = useCallback(() => {
    if (publicTrip) {
      openModal(CopyPublicTrip, {
        publicTrip,
      })
    }
  }, [openModal, publicTrip])

  const onSaveClick = usePendingLoginHandler(
    onSave,
    'tripPlannerLogin',
    trackTripLoginModalSignUpView,
    trackTripLoginModalSignUpDismiss,
  )

  const { removeImmersiveMode } = useImmersiveModeHandlers()
  // currentTripId is used to show the immersive header. It should only be
  // set on actions we want to be immersive. Since it persists on return
  // we make sure to clear it.
  useEffect(() => {
    removeImmersiveMode()
  }, [removeImmersiveMode])

  const { name: tripName = '' } = publicTrip ?? {}

  const containerRef = useRef<HTMLDivElement>(null)

  const leftPaneWidthPercentage = useRef<number>(useAppSelector(state => state.tripPlanner.leftPaneWidth))

  const handleDragStart = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()

    const handleDragging = (e: MouseEvent) => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const containerWidth = rect.width
      const minLeft = 500
      const maxLeft = containerWidth - 500
      const newWidth = clamp(minLeft, e.clientX - rect.left, maxLeft)
      const newWidthPercentage = 100 * (newWidth / containerWidth)
      leftPaneWidthPercentage.current = newWidthPercentage
      // Not doing this via state because it would cause rapid re-renders
      containerRef.current.style.setProperty(LEFT_PANE_WIDTH_VAR, `${newWidthPercentage}%`)
    }

    const handleDragEnd = () => {
      window.removeEventListener('mousemove', handleDragging)
      window.removeEventListener('mouseup', handleDragEnd)
      dispatch(setLeftPaneWidth(leftPaneWidthPercentage.current))
    }

    window.addEventListener('mousemove', handleDragging)
    window.addEventListener('mouseup', handleDragEnd)
  }, [dispatch])

  const tripContextValue = useMemo<TripContextValue>(
    () => ({
      tripId,
      isPublicTrip: true,
      permissions: {
        canEdit: false,
        canAdmin: false,
        canPublish: false,
      },
      name: tripName,
      displayContext: 'tripPlanner',
    }),
    [tripId, tripName],
  )

  if (isLoading) return <LoadingPage />
  if (isError && tripError.status === 404)
  { return (
    <>
      <HeaderBasic
        backTo="/trip-planner"
        backLabel={intl.formatMessage({
          id: 'yYnGrc_YMmoK7ZvmJWc2',
          defaultMessage: 'Trips',
          description: 'Back navigation label to return to trips list from 404 page',
        })}
      />
      <Error404PageWithOffers />
    </>
  ) }
  if (isError) return <Redirect to="/trip-planner" />

  const tripHasDates = !!(publicTrip.startDate && publicTrip.endDate)

  let tabContent: React.ReactElement | null = null
  let isShowingItinerary = false
  if (!tripHasDates) {
    tabContent = <PublicTripSummary trip={publicTrip} />
  } else {
    tabContent = (
      <TripItinerary
        trip={publicTrip as FullTrip}
        tripLoading={isLoading}
      />
    )
    isShowingItinerary = true
  }

  return (
    <HeaderInViewContextProvider>
      <TripContext.Provider value={tripContextValue}>
        <ViewModeContextProvider>
          <ModalProvider>
            <ModalProvider>
              <HeadData
                title={publicTrip.name}
                description={intl.formatMessage(
                  {
                    id: 'XxkiaJwYXYq3Ooc3Powk',
                    defaultMessage: 'View {tripName} on Trip Planner',
                    description: 'Meta description for a public trip page with the trip name',
                  },
                  { tripName: publicTrip.name },
                )}
                imageId={publicTrip?.imageId}
              />
              <TripModal />
              <PublicTripPageHeader publicTrip={publicTrip} />
              <Root ref={containerRef} style={{ [LEFT_PANE_WIDTH_VAR]: `${leftPaneWidthPercentage.current}%` }}>
                <Breakpoint max="tablet">
                  <FloatingContainer>
                    <FloatingActionButton
                      isCollapsed
                      variant="ghost"
                      startIcon={<LineMapIcon />}
                      onClick={() =>
                        openModal(MobileTripMap, {
                          title: tripName,
                          tab: 'Summary',
                          trip: publicTrip,
                          canEdit: false,
                          useCardCarousel: true,
                          isTripLoading: isLoading,
                        })}
                    >
                      {intl.formatMessage({
                        id: '7fRT7TE-9RBz2x0-Tcd_',
                        defaultMessage: 'Map',
                        description: 'Floating action button label to open the trip map view',
                      })}
                    </FloatingActionButton>
                    {isShowingItinerary && <FloatingViewModeToggle />}
                    <FloatingActionButton
                      onClick={onSaveClick}
                      isCollapsed
                      variant="ghost"
                      startIcon={<LineHeartIcon />}
                    >
                      {intl.formatMessage({
                        id: 'M_cBh-1H9v7ozNQB1J4_',
                        defaultMessage: 'Save trip',
                        description: 'Floating action button label to save a public trip to user account',
                      })}
                    </FloatingActionButton>
                  </FloatingContainer>
                </Breakpoint>
                <Content>
                  <PublicTripParentHeader
                    trip={publicTrip}
                  />
                  {tabContent}
                </Content>
                <PaneResizer onMouseDown={handleDragStart} />
                <Breakpoint min="desktop">
                  <MapContainer>
                    <DesktopTripMap
                      title={tripName}
                      tab="Itinerary"
                      trip={publicTrip}
                      canEdit={false}
                      useCardCarousel={false}
                      isTripLoading={isLoading}
                    />
                  </MapContainer>
                </Breakpoint>
              </Root>
            </ModalProvider>
          </ModalProvider>
        </ViewModeContextProvider>
      </TripContext.Provider>
    </HeaderInViewContextProvider>
  )
}

export default PublicTripParent
